import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Flame } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../lib/utils';

const MenuItemCard = ({ item }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const getLocalizedDescription = () => {
    const lang = i18n.language;
    if (lang === 'it' && item.description_it) return item.description_it;
    if (lang === 'de' && item.description_de) return item.description_de;
    if (lang === 'fr' && item.description_fr) return item.description_fr;
    if (lang === 'ar' && item.description_ar) return item.description_ar;
    return item.description;
  };

  // Item names always shown in English (as created)
  const getLocalizedName = () => item.name;

  const SpiceLevel = ({ level }) => {
    if (!level) return null;
    return (
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <Flame key={i} className={`h-3 w-3 ${i < level ? 'text-red-500' : 'text-zinc-700'}`} fill={i < level ? 'currentColor' : 'none'} />
        ))}
      </div>
    );
  };

  return (
    <div
      className="group relative overflow-hidden rounded-2xl bg-[#141417] border border-[#27272A] hover:border-[#FFC107]/60 transition-all duration-300 hover:shadow-[0_0_25px_rgba(255,193,7,0.18)] hover:-translate-y-1 menu-card cursor-pointer flex flex-col"
      data-testid={`menu-item-${item.id}`}
      onClick={() => navigate(`/menu/item/${item.id}`)}
    >
      <div className="aspect-[4/3] relative overflow-hidden">
        {item.image ? (
          <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full bg-[#1C1C22] flex items-center justify-center">
            <span className="text-zinc-600 text-4xl">🍽</span>
          </div>
        )}
        {item.is_popular && (
          <Badge className="absolute top-3 left-3 bg-[#FFC107] text-black font-bold uppercase text-xs">{t('menu.popular')}</Badge>
        )}
        {!item.is_available && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <Badge variant="secondary" className="text-lg">{t('menu.soldOut')}</Badge>
          </div>
        )}
      </div>
      
      <div className="p-2.5 md:p-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-1 mb-1 md:mb-2">
          <h3 className="font-semibold text-sm md:text-lg text-white leading-tight line-clamp-2">{getLocalizedName()}</h3>
          <span className="bg-[#FFC107] text-black font-extrabold text-xs md:text-sm px-2 py-0.5 md:px-2.5 md:py-1 rounded-full whitespace-nowrap shadow-md shadow-amber-400/20">{formatPrice(item.price)}</span>
        </div>
        <p className="text-zinc-400 text-xs md:text-sm line-clamp-2 mb-1 md:mb-2">{getLocalizedDescription()}</p>
        <p className="text-[9px] md:text-[10px] text-amber-400/70 mb-0.5 line-clamp-1" data-testid="allergy-note">
          {t('itemDetail.allergyWarning')}
        </p>
        <p className="text-[9px] md:text-[10px] text-zinc-500 mb-2 md:mb-3 line-clamp-1" data-testid="image-disclaimer">
          {t('menu.imageDisclaimer')}
        </p>
        <div className="flex items-center mt-auto">
          <SpiceLevel level={item.spice_level} />
          <Button
            onClick={(e) => { e.stopPropagation(); navigate(`/menu/item/${item.id}`); }}
            disabled={!item.is_available}
            className="h-8 md:h-10 px-2 md:px-4 bg-[#FFC107] text-black hover:bg-[#F59E0B] font-bold uppercase text-xs md:text-sm tracking-wide transition-all hover:scale-105 active:scale-95 ml-auto"
            data-testid={`add-to-cart-${item.id}`}
          >
            <Plus className="h-4 w-4 md:mr-1" />
            <span className="hidden md:inline">{t('menu.add')}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MenuItemCard;
